import React, { type JSX } from 'react';
import moment from 'moment-timezone';

import { Grid, Stack } from '@mui/material';
import { TimeSlotGrid } from './TimeSlotGrid';
import { WeekEventGrid } from './WeekEventGrid';
import { WeekViewHeader } from './WeekViewHeader';
import { createNumArray } from 'utils/helpers';
import { useTypedSelector } from '@fiji/common';
import { WeekViewCurrentTime } from './WeekViewCurrentTime';
import { useTransformData } from 'hooks/useTransformData';

export const WeekView = (): JSX.Element => {
    const gridRef: any = React.useRef<any>(null);
    const [gridWidth, setGridWidth] = React.useState(0);

    const schedules = useTypedSelector((state) => state.schedule?.schedules);
    const currentTime = useTypedSelector((state) => state.schedule?.currentTime);
    const selectedWeekDays = useTypedSelector((state) => state.schedule?.selectedWeekDays);
    const { timezone: selectedTimezone } = useTransformData(); // timezone from redux

    const updateGridWidth = (): void => {
        if (gridRef.current) {
            setGridWidth(gridRef?.current?.offsetWidth);
        }
    };

    React.useEffect(() => {
        updateGridWidth();
        window.addEventListener('resize', updateGridWidth);
        return () => {
            window.removeEventListener('resize', updateGridWidth);
        };
    }, []);

    const currentDayOfMonth = moment.tz(currentTime, selectedTimezone).date(); // use timezone-aware date

    return (
        <div
            style={{ position: 'relative', paddingTop: 76, paddingLeft: 24, overflow: 'auto' }}
            className="margin-top-36"
        >
            <Grid container>
                <Grid size={0.5}>
                    <TimeSlotGrid />
                </Grid>
                <Grid size={11.5}>
                    <WeekViewHeader />

                    <Grid sx={{ position: 'relative' }} container ref={gridRef}>
                        {selectedWeekDays?.some((item: any) => item?.dayOfMonth === currentDayOfMonth) && (
                            <WeekViewCurrentTime />
                        )}

                        {createNumArray(24)?.map((hour) =>
                            selectedWeekDays?.map((day: any) => (
                                <Grid key={`${hour}${day?.dayOfMonth}`} size={12 / 7}>
                                    <Stack
                                        style={{
                                            height: 60,
                                            display: 'flex',
                                            margin: 0,
                                            ...(day?.dayOfMonth === currentDayOfMonth && {
                                                backgroundColor: '#f2f8fc',
                                            }),
                                        }}
                                        className="border-right-1 border-bottom-1"
                                    />
                                </Grid>
                            ))
                        )}

                        {schedules?.map((schedule: any) => (
                            <WeekEventGrid key={schedule?.id} event={schedule} gridWidth={gridWidth} />
                        ))}
                    </Grid>
                </Grid>
            </Grid>
        </div>
    );
};
